import { useCallback, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import { changeVaultManagerAddress } from 'src/pages/admin/services/vaultService';

import { VAULTS_NS as NS } from './constants';
import {
  confirmsManagerAddress,
  managerAddressErrorMessages,
  validateManagerAddress,
  validateManagerAddressReason,
} from './helpers';

export interface ManagerAddressAlert {
  severity: 'error';
  messages: string[];
}

/**
 * XBIT-4258: the "change the manager address" dialog — the new address, a reason, and the vault's id typed to confirm. One
 * request at a time; whatever the answer, the vault is read again (onSettled). A replay (applied=false: the address was
 * already the manager's) is reported as such, not as a change.
 */
export const useVaultManagerAddress = ({
  vaultId,
  onSettled,
}: {
  vaultId: string;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [isOpen, setOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [errors, setErrors] = useState<{ address?: string; reason?: string }>({});
  const [alert, setAlert] = useState<ManagerAddressAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // From the submit until the vault has been read again: the block's control is the old one until then.
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const open = useCallback(() => {
    setAddress('');
    setReason('');
    setTyped('');
    setErrors({});
    setAlert(null);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    if (!inFlight.current) setOpen(false);
  }, []);

  const confirmed = confirmsManagerAddress(typed, vaultId);

  const submit = useCallback(async () => {
    if (inFlight.current || !isOpen || !confirmed) return;
    const next = {
      address: validateManagerAddress(address),
      reason: validateManagerAddressReason(reason),
    };
    setErrors(next);
    if (next.address || next.reason) return;
    inFlight.current = true;
    setSubmitting(true);
    setBusy(true);
    try {
      const change = await changeVaultManagerAddress(vaultId, address.trim(), reason.trim());
      setOpen(false);
      notify(
        translate(`${NS}.detail.managerAddress.done.${change.applied ? 'applied' : 'replay'}`),
        {
          type: change.applied ? 'success' : 'info',
        }
      );
    } catch (err) {
      setAlert({ severity: 'error', messages: managerAddressErrorMessages(err, translate) });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      try {
        await onSettled();
      } finally {
        setBusy(false);
      }
    }
  }, [address, confirmed, isOpen, notify, onSettled, reason, translate, vaultId]);

  return {
    isOpen,
    address,
    setAddress,
    reason,
    setReason,
    typed,
    setTyped,
    confirmed,
    errors,
    alert,
    submitting,
    busy,
    open,
    close,
    submit,
  };
};

export type VaultManagerAddressForm = ReturnType<typeof useVaultManagerAddress>;
